import { Inject, Injectable } from '@angular/core';
import { EvaluationContext, FeatureFlag, Rule, Status } from '../models/feature-flag.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * A client-side implementation of the feature flag service.
 * This service can evaluate feature flags locally without making API calls.
 * It's useful for reducing network calls or for offline capability.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagClientService {
  constructor(@Inject(CbreLoggerToken) private logger: CbreLogger) {}
  private flags: Map<string, FeatureFlag> = new Map();

  /**
   * Manually initialize the feature flag client with preloaded flags
   * @param flags Array of feature flags
   */
  initialize(flags: FeatureFlag[]): void {
    this.flags.clear();
    flags.forEach(flag => {
      this.flags.set(flag.id, flag);
    });
  }

  /**
   * Add or update a feature flag
   * @param flag The feature flag to add or update
   */
  setFlag(flag: FeatureFlag): void {
    this.flags.set(flag.id, flag);
  }

  /**
   * Check if a feature flag exists
   * @param id The feature flag ID
   */
  exists(id: string): boolean {
    return this.flags.has(id);
  }

  /**
   * Get a feature flag by ID
   * @param id The feature flag ID
   */
  getFeatureFlag(id: string): FeatureFlag | null {
    return this.flags.get(id) || null;
  }

  /**
   * List all feature flags, optionally filtered by status
   * @param status Optional status filter
   */
  listFeatureFlags(status?: Status): FeatureFlag[] {
    const result: FeatureFlag[] = [];

    this.flags.forEach(flag => {
      if (!status || flag.status === status) {
        result.push(flag);
      }
    });

    return result;
  }

  /**
   * Check if a feature flag is enabled for the given context
   * @param flagId The feature flag ID
   * @param context Optional context for evaluation
   */
  isEnabled(flagId: string, context?: EvaluationContext): boolean {
    const flag = this.getFeatureFlag(flagId);

    // If flag not found or archived, return false
    if (!flag || flag.status === 'archived') {
      return false;
    }

    // If no rule defined, default to false
    if (!flag.rule) {
      return false;
    }

    try {
      return this.evaluateRule(flag.rule, context || {});
    } catch (error) {
      this.logger.error(`Error evaluating flag ${flagId}: ${error?.message ?? error}`);
      return false; // Default to false on error
    }
  }

  /**
   * Check if a feature flag is enabled with a default fallback value
   * @param flagId The feature flag ID
   * @param context Optional context for evaluation
   * @param defaultValue Value to use if the flag doesn't exist or evaluation fails
   */
  isEnabledWithDefault(flagId: string, context?: EvaluationContext, defaultValue = false): boolean {
    try {
      // If flag doesn't exist, use the default value
      if (!this.exists(flagId)) {
        return defaultValue;
      }

      return this.isEnabled(flagId, context);
    } catch (error) {
      this.logger.error(`Error in isEnabledWithDefault for flag ${flagId}: ${error?.message ?? error}`);
      return defaultValue;
    }
  }

  /**
   * Evaluate a rule against the given context
   * This method implements the exact same logic as the backend ServiceImpl.evaluateRule
   * @param rule The rule to evaluate
   * @param context The context for evaluation
   */
  private evaluateRule(rule: Rule, context: EvaluationContext): boolean {
    switch (rule.type) {
      case 'boolean':
        return this.evaluateBooleanRule(rule);

      case 'email':
        return this.evaluateEmailRule(rule, context);

      case 'userGroup':
        return this.evaluateUserGroupRule(rule, context);

      case 'region':
        return this.evaluateRegionRule(rule, context);

      case 'lineOfBusiness':
        return this.evaluateLineOfBusinessRule(rule, context);

      case 'group':
        return this.evaluateGroupRule(rule, context);

      default:
        throw new Error(`Unsupported rule type: ${(rule as Rule).type}`);
    }
  }

  private evaluateBooleanRule(rule: Rule): boolean {
    return !!(rule as any).value;
  }

  private evaluateEmailRule(rule: Rule, context: EvaluationContext): boolean {
    if (!context.email || !(rule as any).values?.length) {
      return false;
    }

    return (rule as any).values.some((value: string) => value.toLowerCase() === context.email.toLowerCase());
  }

  private evaluateUserGroupRule(rule: Rule, context: EvaluationContext): boolean {
    if (!context.userGroups?.length || !(rule as any).values?.length) {
      return false;
    }

    const userGroupsMap: Record<string, boolean> = context.userGroups.reduce(
      (map, group) => {
        map[group.toLowerCase()] = true;
        return map;
      },
      {} as Record<string, boolean>
    );

    return (rule as any).values.some((value: string) => userGroupsMap[value.toLowerCase()]);
  }

  private evaluateRegionRule(rule: Rule, context: EvaluationContext): boolean {
    if (!context.region || !(rule as any).values?.length) {
      return false;
    }

    return (rule as any).values.some((value: string) => value.toLowerCase() === context.region.toLowerCase());
  }

  private evaluateLineOfBusinessRule(rule: Rule, context: EvaluationContext): boolean {
    if (!context.lineOfBusiness || !(rule as any).values?.length) {
      return false;
    }

    return (rule as any).values.some((value: string) => value.toLowerCase() === context.lineOfBusiness.toLowerCase());
  }

  private evaluateGroupRule(rule: Rule, context: EvaluationContext): boolean {
    if (!(rule as any).childRules?.length) {
      return false;
    }

    const results = (rule as any).childRules.map((childRule: Rule) => this.evaluateRule(childRule, context));

    if ((rule as any).operator === 'AND') {
      return results.every(result => result);
    } else if ((rule as any).operator === 'OR') {
      return results.some(result => result);
    }

    throw new Error('Unsupported operator');
  }
}
